import { useState, useEffect, useLayoutEffect, useRef, useCallback, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Plus, Search, Trash2, X, CheckCircle, AlertCircle,
  Loader2, Brain, FileText,
  Calendar, Tag, Type, CircleDot, TrendingUp, AlignLeft,
  Link as LinkIcon, ExternalLink, DollarSign,
  ChevronDown, Check, ThumbsUp, Megaphone, Radio,
  Newspaper, LayoutTemplate, Library, Eye, Package, Store, StickyNote, ClipboardCheck,
  List, HelpCircle, CheckSquare, Square, FlaskConical, LayoutGrid, Users, Mic2,
  GripVertical, ArrowUpRight, Tags,
} from 'lucide-react';
import { ProductEconomicsInput } from './lab-dashboard/ProductEconomicsInput';
import type { ProductEconomics } from '../lib/product-economics';
import { ReviewsFileInput } from './lab-dashboard/ReviewsFileInput';
import { LabBrandsPage } from './LabBrandsPage';
import { labApi } from '../lib/api';
import { createProductAutosave } from '../lib/product-autosave';
import { EmptyState } from '../components/EmptyState';
import { useConfirmDialog } from '../contexts/ConfirmDialogContext';
import { Button, buttonVariants } from '../components/ui/Button';
import { Select } from '../components/ui/Select';
import { LabCreateWorkflowModal } from './lab-dashboard/LabCreateWorkflowModal';

// ─── Types ──────────────────────────────────────────────────────────────────

interface LabProduct {
  id: string;
  name: string;
  description: string | null;
  niche: string | null;
  potential: 'STANDARD' | 'HIGH' | null;
  status: string;
  testStatus: string;
  sortOrder: number;
  createdAt: string;
  imageUrl?: string | null;
  competitorLinks: string | null;
  marketplaceLink: string | null;
  otherSources: string | null;
  adTranscripts: string[] | null;
  pricePackages: PricePackage[] | null;
  customFields: Record<string, unknown> | null;
  addedToLab?: boolean;
  brandId?: string | null;
  knowledgeBases: Array<{ id: string; name: string; isComplete: boolean }>;
  contents: Array<{ id: string; type: string; title?: string | null; version: number; status: string; createdAt?: string; knowledgeBaseId?: string | null }>;
  _count: { contents: number };
}

interface PricePackage {
  economics?: ProductEconomics;
  name: string;
  oldPrice: string;
  newPrice: string;
}

interface FieldConfig {
  key: string;
  label: string;
  type: string;
  icon?: string;
  showInTable: boolean;
  width?: number;
  isCustom?: boolean;
  allowDuplicate?: boolean;
}

type FieldValueUpdater = unknown | ((currentValue: unknown) => unknown);

interface FieldOption {
  value: string;
  label: string;
  color: string;
}

interface LabBrandOption {
  id: string;
  name: string;
  productCount: number;
}

// ─── Icon Map ───────────────────────────────────────────────────────────────

const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  Calendar, Tag, Type, CircleDot, TrendingUp, AlignLeft,
  Link: LinkIcon, ExternalLink, DollarSign,
  FileText, ThumbsUp, Megaphone, Radio, Newspaper,
  LayoutTemplate, Library, Eye, Package, Store, StickyNote,
  ClipboardCheck,
};

function FieldIcon({ name, className }: { name?: string; className?: string }) {
  if (!name) return null;
  const Icon = ICON_MAP[name];
  return Icon ? <Icon className={className} /> : null;
}

function formatLabDate(value?: string | null): string {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${day}.${month}.${date.getFullYear()}`;
}

// ─── Constants ──────────────────────────────────────────────────────────────

const DEFAULT_TEST_STATUS_OPTIONS: FieldOption[] = [
  { value: 'PENDING', label: '⏳ Oczekujący', color: '#9ca3af' },
  { value: 'TO_TEST', label: '🧪 Do testów', color: '#34d399' },
  { value: 'TESTING', label: '🔬 W trakcie testów', color: '#38bdf8' },
  { value: 'TEST_SUCCESS', label: '🏆 Udany test', color: '#4ade80' },
  { value: 'TEST_FAILED', label: '💩 Negatywny test', color: '#f87171' },
];

const POTENTIAL_CONFIG: Record<string, { label: string; bg: string; text: string }> = {
  STANDARD: { label: '👌 Standardowy', bg: 'rgba(34,197,94,0.2)', text: '#4ade80' },
  HIGH: { label: '🔥 Wysoki', bg: 'rgba(249,115,22,0.2)', text: '#f97316' },
};

const POTENTIAL_OPTIONS = [
  { value: 'STANDARD', label: '👌 Standardowy', color: '#4ade80' },
  { value: 'HIGH', label: '🔥 Wysoki', color: '#f97316' },
];

const LAB_STATUS_CONFIG: Record<string, { label: string; bg: string; text: string }> = {
  DRAFT: { label: 'Szkic', bg: 'rgba(107,114,128,0.16)', text: '#9ca3af' },
  INPUT: { label: 'Dane uzupełnione', bg: 'rgba(74,158,255,0.16)', text: '#4a9eff' },
  ANALYZING: { label: 'Analiza', bg: 'rgba(245,158,11,0.16)', text: '#f59e0b' },
  KNOWLEDGE_READY: { label: 'Baza wiedzy gotowa', bg: 'rgba(16,185,129,0.16)', text: '#10b981' },
  GENERATING: { label: 'Generowanie', bg: 'rgba(59,130,246,0.16)', text: '#3b82f6' },
  COMPLETE: { label: 'Kompletne', bg: 'rgba(16,185,129,0.16)', text: '#10b981' },
};

const LAB_STATUS_OPTIONS = Object.entries(LAB_STATUS_CONFIG).map(([value, cfg]) => ({
  value,
  label: cfg.label,
  color: cfg.text,
}));

const KNOWN_DB_KEYS = new Set([
  'name', 'description', 'niche', 'potential', 'testStatus', 'storeId', 'brandId',
  'competitorLinks', 'marketplaceLink', 'otherSources',
  'adTranscripts', 'pricePackages', 'demographicsImage', 'reviewsCsv',
  'scrapedContent', 'sortOrder', 'status', 'customFields', 'primaryMarketplaceUrl', 'primaryWholesaleUrl',
]);
const HIDDEN_PRODUCT_FIELD_KEYS = new Set(['status']);

const PACKAGE_QTY_OPTIONS = ['1 sztuka', '2 sztuki', '3 sztuki', '4 sztuki', '5 sztuk', 'Inne'];

// ─── Asset icons helper ─────────────────────────────────────────────────────

const ASSET_ICONS: { type: string; icon: React.ComponentType<{ className?: string }>; color: string; label: string }[] = [
  { type: 'KNOWLEDGE', icon: Brain, color: 'text-emerald-400', label: 'Baza wiedzy' },
  { type: 'LANDING', icon: FileText, color: 'text-accent-info', label: 'Produktowe' },
  { type: 'LISTICALE', icon: List, color: 'text-purple-400', label: 'Listical' },
  { type: 'ADVERTORIAL', icon: Megaphone, color: 'text-orange-400', label: 'Advertorial' },
  { type: 'QUIZ', icon: HelpCircle, color: 'text-pink-400', label: 'Quiz' },
];

function AssetIcons({ product }: { product: LabProduct }) {
  const hasKB = product.knowledgeBases?.some(kb => kb.isComplete);
  const isAnalyzing = product.status === 'ANALYZING' && product.knowledgeBases?.some(kb => !kb.isComplete);
  const contentTypes = new Set(
    (product.contents || []).filter(c => c.status === 'COMPLETE').map(c => c.type)
  );

  return (
    <div className="flex items-center gap-1">
      {isAnalyzing && (
        <Link
          to={`/marketing/laboratorium/produkt/${product.id}`}
          onClick={e => e.stopPropagation()}
          title="Budowanie bazy wiedzy — kliknij, aby wrócić"
          className="flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-400 text-[10px] font-medium hover:bg-amber-500/25 transition-colors"
        >
          <Loader2 className="w-3 h-3 animate-spin" />
          Budowanie...
        </Link>
      )}
      {ASSET_ICONS.map(({ type, icon: Icon, color, label }) => {
        const active = type === 'KNOWLEDGE' ? hasKB : contentTypes.has(type);
        if (!active) return null;
        return (
          <span key={type} title={label}>
            <Icon className={`w-3.5 h-3.5 ${color}`} />
          </span>
        );
      })}
    </div>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function getProductValue(product: LabProduct, key: string): unknown {
  if (KNOWN_DB_KEYS.has(key) || ['id', 'createdAt', 'knowledgeBases', 'contents', '_count', 'addedToLab'].includes(key)) {
    return (product as unknown as Record<string, unknown>)[key];
  }
  return product.customFields?.[key] ?? null;
}

function createProductPopupForm(product: LabProduct, fields: FieldConfig[]): Record<string, unknown> {
  const initial: Record<string, unknown> = {};
  for (const field of fields) {
    initial[field.key] = getProductValue(product, field.key) ?? '';
  }
  initial.brandId = product.brandId || null;
  return initial;
}

function getProductFieldDuplicates(product: LabProduct): Record<string, string[]> {
  const customFields = product.customFields as Record<string, unknown> | null | undefined;
  const duplicates = customFields?._fieldDuplicates;
  if (!duplicates || typeof duplicates !== 'object' || Array.isArray(duplicates)) return {};
  return Object.fromEntries(
    Object.entries(duplicates)
      .filter((entry): entry is [string, string[]] => (
        Array.isArray(entry[1]) && entry[1].every(value => typeof value === 'string')
      ))
      .map(([key, values]) => [key, [...values]]),
  );
}

function hasLaboratory(product: LabProduct): boolean {
  return Boolean(
    product.addedToLab ||
    product.knowledgeBases?.length ||
    product.contents?.length,
  );
}

function getTestStatusOptions(fieldOptions: Record<string, FieldOption[]>): FieldOption[] {
  return fieldOptions.testStatus?.length ? fieldOptions.testStatus : DEFAULT_TEST_STATUS_OPTIONS;
}

function getOptionDisplayConfig(
  options: FieldOption[],
  value: string | null | undefined,
  fallbackValue: string,
): { label: string; bg: string; text: string } {
  const option = options.find(opt => opt.value === value) || options.find(opt => opt.value === fallbackValue);
  const color = option?.color || '#9ca3af';
  return {
    label: option?.label || value || '—',
    bg: `${color}20`,
    text: color,
  };
}

function canDuplicateField(field: FieldConfig): boolean {
  if (!field.allowDuplicate) return false;
  if (field.key === 'ads' || field.key === 'broadcast') return false;
  return true;
}

function normalizeLabProductFields(fields: FieldConfig[]): FieldConfig[] {
  const statusField = fields.find(field => field.key === 'status');
  const testStatusField = fields.find(field => field.key === 'testStatus');
  const visible = fields.filter(field => !HIDDEN_PRODUCT_FIELD_KEYS.has(field.key) && field.key !== 'testStatus');


  const normalizedTestStatus = testStatusField && {
    ...testStatusField,
    label: testStatusField.label || 'Status',
    showInTable: testStatusField.showInTable || Boolean(statusField?.showInTable),
  };
  const insertAt = statusField ? fields.findIndex(field => field.key === 'status') : fields.findIndex(field => field.key === 'testStatus');
  if (normalizedTestStatus) visible.splice(Math.max(0, Math.min(insertAt, visible.length)), 0, normalizedTestStatus);
  const reviewIndex = visible.findIndex(field => field.key === 'reviewsCsv');
  if (reviewIndex >= 0) visible.splice(reviewIndex, 1);
  const spyIndex = visible.findIndex(field => field.key === 'spytool');
  visible.splice(spyIndex >= 0 ? spyIndex : visible.length, 0, {
    key: 'reviewsCsv', label: 'Opinie klientów', type: 'reviews', icon: 'FileText', showInTable: false,
  });
  return visible;
}

function shortenUrl(url: string): string {
  try {
    const u = new URL(url);
    const path = u.pathname.length > 20 ? u.pathname.slice(0, 20) + '...' : u.pathname;
    return u.hostname + path;
  } catch {
    return url.length > 40 ? url.slice(0, 40) + '...' : url;
  }
}

// ─── Custom Select Dropdown ─────────────────────────────────────────────────

function ColorSelect({
  value,
  options,
  onChange,
  placeholder = '— wybierz —',
}: {
  value: string;
  options: { value: string; label: string; color: string }[];
  onChange: (val: string) => void;
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selected = options.find(o => o.value === value);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 w-full px-3 py-1.5 bg-surface-hover border border-border rounded-lg text-sm text-left hover:border-border-strong transition-colors"
      >
        {selected ? (
          <span
            className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium"
            style={{ backgroundColor: `${selected.color}20`, color: selected.color }}
          >
            {selected.label}
          </span>
        ) : (
          <span className="text-text-muted">{placeholder}</span>
        )}
        <ChevronDown className="w-3.5 h-3.5 text-text-muted ml-auto" />
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full bg-surface-hover border border-border rounded-lg shadow-xl py-1 max-h-48 overflow-y-auto">
          <button
            onClick={() => { onChange(''); setOpen(false); }}
            className="w-full px-3 py-1.5 text-left text-sm text-text-muted hover:bg-surface-muted transition-colors"
          >
            {placeholder}
          </button>
          {options.map(opt => (
            <button
              key={opt.value}
              onClick={() => { onChange(opt.value); setOpen(false); }}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-surface-muted transition-colors"
            >
              <span
                className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium"
                style={{ backgroundColor: `${opt.color}20`, color: opt.color }}
              >
                {opt.label}
              </span>
              {opt.value === value && <Check className="w-3 h-3 text-accent-primary ml-auto" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Inline Cell Select (compact for table) ─────────────────────────────────

function InlineCellSelect({
  value,
  options,
  onChange,
  onClose,
  anchorElement,
}: {
  value: string;
  options: { value: string; label: string; color: string }[];
  onChange: (val: string) => void;
  onClose: () => void;
  anchorElement: HTMLElement;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [popupPosition, setPopupPosition] = useState<{
    top: number;
    left: number;
    width: number;
    maxHeight: number;
    placement: 'top' | 'bottom';
  } | null>(null);

  useLayoutEffect(() => {
    const updatePosition = () => {
      const rect = anchorElement.getBoundingClientRect();
      const viewportMargin = 8;
      const gap = 4;
      const desiredHeight = Math.min(192, options.length * 36 + 8);
      const availableBelow = window.innerHeight - rect.bottom - gap - viewportMargin;
      const availableAbove = rect.top - gap - viewportMargin;
      const placement = availableBelow < Math.min(desiredHeight, 120) && availableAbove > availableBelow
        ? 'top'
        : 'bottom';
      const availableHeight = placement === 'top' ? availableAbove : availableBelow;
      const maxHeight = Math.max(48, Math.min(desiredHeight, availableHeight));
      const width = Math.min(Math.max(140, rect.width), window.innerWidth - 2 * viewportMargin);
      const left = Math.max(
        viewportMargin,
        Math.min(rect.left, window.innerWidth - width - viewportMargin),
      );

      setPopupPosition({
        top: placement === 'top'
          ? Math.max(viewportMargin, rect.top - gap - maxHeight)
          : rect.bottom + gap,
        left,
        width,
        maxHeight,
        placement,
      });
    };

    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [anchorElement, options.length]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  if (!popupPosition) return null;

  return createPortal(
    <div
      ref={ref}
      className={`fixed z-[9999] overflow-y-auto rounded-lg border border-border bg-surface-hover py-1 shadow-xl ${
        popupPosition.placement === 'top' ? 'origin-bottom' : 'origin-top'
      }`}
      style={{
        top: popupPosition.top,
        left: popupPosition.left,
        width: popupPosition.width,
        maxHeight: popupPosition.maxHeight,
      }}
    >
      {options.map(opt => (
        <button
          key={opt.value}
          onClick={() => { onChange(opt.value); onClose(); }}
          className="w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-surface-muted transition-colors"
        >
          <span
            className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium"
            style={{ backgroundColor: `${opt.color}20`, color: opt.color }}
          >
            {opt.label}
          </span>
          {opt.value === value && <Check className="w-3 h-3 text-accent-primary ml-auto" />}
        </button>
      ))}
    </div>,
    document.body
  );
}

// ─── Inline Cell Editor ─────────────────────────────────────────────────────

function InlineCellEditor({
  value,
  onSave,
  onCancel,
}: {
  value: string;
  onSave: (val: string) => void;
  onCancel: () => void;
}) {
  const [text, setText] = useState(value);
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    ref.current?.focus();
    ref.current?.select();
  }, []);

  return (
    <input
      ref={ref}
      value={text}
      onChange={e => setText(e.target.value)}
      onKeyDown={e => {
        if (e.key === 'Enter') onSave(text);
        if (e.key === 'Escape') onCancel();
      }}
      onBlur={() => onSave(text)}
      className="w-full px-1.5 py-0.5 bg-surface-muted border border-accent-primary/40 rounded text-text-primary text-sm focus:outline-none"
    />
  );
}

// ─── Cell Value (display mode) ──────────────────────────────────────────────

function CellValue({ product, field, fieldOptions }: {
  product: LabProduct;
  field: FieldConfig;
  fieldOptions: Record<string, FieldOption[]>;
}) {
  const value = getProductValue(product, field.key);

  if (field.type === 'date') {
    return (
      <span className="text-xs text-text-muted">
        {formatLabDate(value as string | null)}
      </span>
    );
  }

  if (field.key === 'status') {
    const cfg = LAB_STATUS_CONFIG[value as string];
    if (!cfg) return <span className="text-xs text-text-secondary">{(value as string) || '—'}</span>;
    return (
      <span
        className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium whitespace-nowrap"
        style={{ backgroundColor: cfg.bg, color: cfg.text }}
      >
        {cfg.label}
      </span>
    );
  }

  if (field.type === 'testStatus') {
    const cfg = getOptionDisplayConfig(getTestStatusOptions(fieldOptions), value as string, 'PENDING');
    return (
      <span
        className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium whitespace-nowrap"
        style={{ backgroundColor: cfg.bg, color: cfg.text }}
      >
        {cfg.label}
      </span>
    );
  }

  if (field.type === 'potential') {
    if (!value) return <span className="text-xs text-text-disabled">—</span>;
    const cfg = POTENTIAL_CONFIG[value as string];
    if (!cfg) return <span className="text-xs text-text-muted">{value as string}</span>;
    return (
      <span
        className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium whitespace-nowrap"
        style={{ backgroundColor: cfg.bg, color: cfg.text }}
      >
        {cfg.label}
      </span>
    );
  }

  if (field.type === 'select') {
    if (!value && field.key === 'niche') {
      return (
        <span className="inline-flex items-center rounded-full bg-surface-hover px-2 py-0.5 text-[11px] font-medium text-text-muted whitespace-nowrap">
          Brak kategorii
        </span>
      );
    }
    if (!value) return <span className="text-xs text-text-disabled">—</span>;
    const options = fieldOptions[field.key] || [];
    const opt = options.find(o => o.value === value);
    if (opt) {
      return (
        <span
          className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium whitespace-nowrap"
          style={{ backgroundColor: `${opt.color}20`, color: opt.color }}
        >
          {opt.label}
        </span>
      );
    }
    return <span className="text-xs text-text-secondary">{value as string}</span>;
  }

  if (field.type === 'url') {
    if (!value) return <span className="text-xs text-text-disabled">—</span>;
    return (
      <a
        href={value as string}
        target="_blank"
        rel="noopener noreferrer"
        onClick={e => e.stopPropagation()}
        className="text-xs text-accent-primary/70 hover:text-accent-primary hover:underline truncate block transition-colors"
        title={value as string}
      >
        {shortenUrl(value as string)}
      </a>
    );
  }

  if (field.type === 'number') {
    if (!value && value !== 0) return <span className="text-xs text-text-disabled">—</span>;
    return <span className="text-sm text-text-secondary">{String(value)}</span>;
  }

  if (field.type === 'transcripts') {
    const transcripts = Array.isArray(value) ? value.filter(v => typeof v === 'string' && v.trim()) : [];
    return (
      <span className="text-xs text-text-secondary">
        {transcripts.length ? `${transcripts.length} transkryptów` : '—'}
      </span>
    );
  }

  return (
    <span className="text-sm text-text-secondary truncate block">
      {(value as string) || '—'}
    </span>
  );
}

// ─── Product Popup (Notion style) ───────────────────────────────────────────

export function ProductPopup({
  product,
  fields,
  fieldOptions,
  brands,
  onClose,
  onSave,
  onDelete,
}: {
  product: LabProduct;
  fields: FieldConfig[];
  fieldOptions: Record<string, FieldOption[]>;
  brands: LabBrandOption[];
  onClose: () => void;
  onSave: (id: string, data: Record<string, unknown>) => Promise<void>;
  onDelete: (id: string) => void;
}) {
  const [form, setForm] = useState<Record<string, unknown>>(() => createProductPopupForm(product, fields));
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [duplicatedFields, setDuplicatedFields] = useState<Record<string, string[]>>(() => getProductFieldDuplicates(product));
  const [showLabSetup, setShowLabSetup] = useState(false);
  const [labWorkflowBusy, setLabWorkflowBusy] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const formRef = useRef(form);
  const duplicatedFieldsRef = useRef(duplicatedFields);
  formRef.current = form;
  duplicatedFieldsRef.current = duplicatedFields;

  const [saveError, setSaveError] = useState('');
  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;
  const snapshot = () => ({
    ...formRef.current,
    customFields: { _fieldDuplicates: structuredClone(duplicatedFieldsRef.current) },
  });
  const [autosave] = useState(() => createProductAutosave(snapshot(), patch => onSaveRef.current(product.id, patch)));

  const saveNow = async () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = null;
    setSaving(true);
    setSaveStatus('idle');
    try {
      await autosave.flush(snapshot());
      setSaveStatus('success');
      setSaveError('');
      return true;
    } catch (err) {
      const response = (err as { response?: { data?: { error?: string; details?: Array<{ path: string; message: string }> } } }).response?.data;
      const details = response?.details?.map(item => {
        const label = fields.find(field => field.key === item.path)?.label || item.path;
        return `${label}: ${item.message}`;
      }).join('; ');
      setSaveError(details || (typeof response?.error === 'string' ? response.error : 'Nie udało się zapisać zmian. Spróbuj ponownie.'));
      setSaveStatus('error');
      return false;
    } finally {
      setSaving(false);
    }
  };
  const closeAfterSave = async () => {
    if (!labWorkflowBusy && await saveNow()) onClose();
  };
  const closeRef = useRef(closeAfterSave);
  closeRef.current = closeAfterSave;

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') void closeRef.current();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const triggerAutoSave = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setSaveStatus('idle');
    debounceRef.current = setTimeout(() => { void saveNow(); }, 800);
  };

  const updateField = (key: string, value: FieldValueUpdater) => {
    setForm(prev => {
      const nextValue = typeof value === 'function'
        ? (value as (currentValue: unknown) => unknown)(prev[key])
        : value;
      const next = { ...prev, [key]: nextValue };
      formRef.current = next;
      return next;
    });
    triggerAutoSave();
  };

  const addDuplicate = (fieldKey: string) => {
    setDuplicatedFields(prev => {
      const next = {
        ...prev,
        [fieldKey]: [...(prev[fieldKey] || []), ''],
      };
      duplicatedFieldsRef.current = next;
      return next;
    });
    triggerAutoSave();
  };

  const updateDuplicate = (fieldKey: string, idx: number, value: string) => {
    setDuplicatedFields(prev => {
      const next = {
        ...prev,
        [fieldKey]: (prev[fieldKey] || []).map((v, i) => i === idx ? value : v),
      };
      duplicatedFieldsRef.current = next;
      return next;
    });
    triggerAutoSave();
  };

  const removeDuplicate = (fieldKey: string, idx: number) => {
    setDuplicatedFields(prev => {
      const next = {
        ...prev,
        [fieldKey]: (prev[fieldKey] || []).filter((_, i) => i !== idx),
      };
      duplicatedFieldsRef.current = next;
      return next;
    });
    triggerAutoSave();
  };

  const pricePackages = (form.pricePackages as PricePackage[]) || [];
  const addPricePackage = () => {
    updateField('pricePackages', [...pricePackages, { name: '1 sztuka', oldPrice: '', newPrice: '' }]);
  };
  const updatePricePackage = (idx: number, field: keyof PricePackage, val: string) => {
    updateField('pricePackages', pricePackages.map((p, i) => i === idx ? { ...p, [field]: val } : p));
  };
  const removePricePackage = (idx: number) => {
    updateField('pricePackages', pricePackages.filter((_, i) => i !== idx));
  };

  const adTranscripts = Array.isArray(form.adTranscripts) && form.adTranscripts.length > 0
    ? (form.adTranscripts as string[])
    : [''];
  const updateTranscript = (idx: number, val: string) => {
    updateField('adTranscripts', (currentValue: unknown) => {
      const current = Array.isArray(currentValue) && currentValue.length > 0
        ? currentValue as string[]
        : [''];
      return current.map((item, i) => i === idx ? val : item);
    });
  };
  const addTranscript = () => {
    updateField('adTranscripts', (currentValue: unknown) => {
      const current = Array.isArray(currentValue) && currentValue.length > 0
        ? currentValue as string[]
        : [''];
      return [...current, ''];
    });
  };
  const removeTranscript = (idx: number) => {
    updateField('adTranscripts', (currentValue: unknown) => {
      const current = Array.isArray(currentValue) && currentValue.length > 0
        ? currentValue as string[]
        : [''];
      const next = current.filter((_, i) => i !== idx);
      return next.length > 0 ? next : [''];
    });
  };

  const inputClass = 'w-full px-3 py-1.5 bg-surface-hover border border-border rounded-lg text-text-primary text-sm focus:outline-none focus:border-accent-primary/50 hover:border-border-strong transition-colors';

  const nav = useNavigate();

  const renderFieldValue = (field: FieldConfig) => {
    const value = form[field.key];

    if (field.key === 'status') {
      return <ColorSelect value={(value as string) || 'DRAFT'} options={LAB_STATUS_OPTIONS} onChange={v => updateField(field.key, v || 'DRAFT')} />;
    }

    if (field.type === 'select') {
      const options = fieldOptions[field.key] || [];
      return (
        <ColorSelect
          value={(value as string) || ''}
          options={options}
          onChange={v => updateField(field.key, v)}
          placeholder={field.key === 'niche' ? 'Brak kategorii' : '— wybierz —'}
        />
      );
    }

    if (field.type === 'potential') {
      return <ColorSelect value={(value as string) || ''} options={POTENTIAL_OPTIONS} onChange={v => updateField(field.key, v)} />;
    }

    if (field.type === 'testStatus') {
      return <ColorSelect value={(value as string) || ''} options={getTestStatusOptions(fieldOptions)} onChange={v => updateField(field.key, v)} />;
    }

    if (field.type === 'textarea') {
      return (
        <textarea
          value={(value as string) || ''}
          onChange={e => updateField(field.key, e.target.value)}
          rows={3}
          className={inputClass}
        />
      );
    }

    if (field.type === 'number') {
      return (
        <input
          type="number"
          value={(value as string | number) ?? ''}
          onChange={e => updateField(field.key, e.target.value)}
          className={inputClass}
          placeholder="0"
        />
      );
    }

    if (field.type === 'url') {
      const urlVal = (value as string) || '';
      return (
        <div className="relative">
          <input
            type="text"
            value={urlVal}
            onChange={e => updateField(field.key, e.target.value)}
            className={`${inputClass} ${urlVal ? 'pr-8' : ''}`}
            placeholder="https://..."
          />
          {urlVal && (
            <a
              href={urlVal}
              target="_blank"
              rel="noopener noreferrer"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-text-muted hover:text-accent-primary transition-colors"
              title="Otwórz link"
            >
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          )}
        </div>
      );
    }

    if (field.type === 'transcripts') {
      return (
        <div className="space-y-2">
          {adTranscripts.map((t, i) => (
            <div key={i} className="flex items-start gap-2">
              <textarea
                value={t}
                onChange={e => updateTranscript(i, e.target.value)}
                rows={4}
                className={inputClass}
                placeholder={`Transkrypt reklamy ${i + 1}`}
              />
              {adTranscripts.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeTranscript(i)}
                  className="mt-2 p-1 text-text-disabled hover:text-danger transition-colors shrink-0"
                  title="Usuń transkrypt"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          ))}
          <button
            type="button"
            onClick={addTranscript}
            className="inline-flex items-center gap-1 text-xs font-medium text-accent-primary/80 hover:text-accent-primary transition-colors"
          >
            <Plus className="w-3 h-3" />
            Dodaj transkrypt
          </button>
        </div>
      );
    }

    if (field.key === 'reviewsCsv') {
      return <ReviewsFileInput value={typeof value === 'string' ? value : ''} onChange={text => updateField(field.key, text)} />;
    }

    if (field.type === 'packages') {
      return (
        <div className="space-y-2">
          {pricePackages.map((pkg, i) => {
            const isCustom = !PACKAGE_QTY_OPTIONS.includes(pkg.name) && pkg.name !== 'Inne';
            const selectValue = isCustom && pkg.name ? 'Inne' : pkg.name;
            const hasCustomName = selectValue === 'Inne' || isCustom;
            return (
              <div key={i} className="space-y-1.5">
                <div className="flex gap-2 items-center">
                  <Select
                    value={selectValue}
                    onChange={e => {
                      const v = e.target.value;
                      if (v === 'Inne') {
                        updatePricePackage(i, 'name', '');
                      } else {
                        updatePricePackage(i, 'name', v);
                      }
                    }}
                    className={`${inputClass} flex-1`}
                    placeholder="Ilość"
                  >
                    <option value="">— ilość —</option>
                    {PACKAGE_QTY_OPTIONS.map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </Select>
                  <input type="text" value={pkg.oldPrice} onChange={e => updatePricePackage(i, 'oldPrice', e.target.value)} className={`${inputClass} flex-1`} placeholder="Stara cena" />
                  <input type="text" value={pkg.newPrice} onChange={e => updatePricePackage(i, 'newPrice', e.target.value)} className={`${inputClass} flex-1`} placeholder="Nowa cena" />
                  <button onClick={() => removePricePackage(i)} className="text-danger hover:text-danger text-xs shrink-0 transition-colors">Usuń</button>
                </div>
                <ProductEconomicsInput value={pkg.economics} onChange={economics => updateField('pricePackages', pricePackages.map((item, index) => index === i ? { ...item, economics } : item))} />
                {hasCustomName && (
                  <input
                    type="text"
                    value={pkg.name}
                    onChange={e => updatePricePackage(i, 'name', e.target.value)}
                    className={`${inputClass} w-full`}
                    placeholder="Własna nazwa pakietu np. 10 sztuk"
                  />
                )}
              </div>
            );
          })}
          <button
            type="button"
            onClick={addPricePackage}
            className="inline-flex items-center gap-1 text-xs font-medium text-accent-primary/80 hover:text-accent-primary transition-colors"
          >
            <Plus className="w-3 h-3" />
            Dodaj pakiet
          </button>
        </div>
      );
    }

    if (field.type === 'date') {
      return (
        <span className="text-sm text-text-secondary py-1.5 block">
          {formatLabDate(value as string | null)}
        </span>
      );
    }

    return (
      <input
        type="text"
        value={(value as string) || ''}
        onChange={e => updateField(field.key, e.target.value)}
        className={inputClass}
      />
    );
  };

  // renderActionCard removed — replaced by single "Add to Lab" button

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-10 pb-10">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => { void closeAfterSave(); }} />

      <div className="relative bg-surface-raised rounded-xl border border-border w-full max-w-2xl max-h-[calc(100vh-5rem)] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="shrink-0 flex items-center gap-3 px-6 py-4 border-b border-border">
          <h2 className="text-xl font-bold text-text-primary truncate">{(form.name as string) || product.name}</h2>
          <AssetIcons product={product} />
          <div className="flex-1" />
          <button disabled={labWorkflowBusy || saving} onClick={() => { void closeAfterSave(); }} className="p-1.5 text-text-secondary hover:text-text-primary rounded-lg hover:bg-surface-hover transition-colors disabled:cursor-not-allowed disabled:opacity-40">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto">
          {/* Notion-style fields: icon+label left, value right */}
          <div className="px-6 py-4">
            {fields.flatMap(field => {
              const fieldRow = (
              <div key={field.key} className="group/field">
                <div className="flex items-start gap-3 py-2.5 border-b border-border-subtle hover:bg-surface-hover/20 transition-colors rounded-sm -mx-2 px-2">
                  {/* Label column */}
                  <div className="flex items-center gap-2 w-40 shrink-0 pt-1.5">
                    <FieldIcon name={field.icon} className="w-3.5 h-3.5 text-text-muted" />
                    <span className="text-sm text-text-muted">{field.label}</span>
                    {/* + button to duplicate (on hover, after label) */}
                    {canDuplicateField(field) && (
                      <button
                        type="button"
                        onClick={() => addDuplicate(field.key)}
                        className="p-0.5 text-accent-primary/45 opacity-70 hover:opacity-100 hover:text-accent-primary transition-all"
                        title={`Dodaj kolejne "${field.label}"`}
                      >
                        <Plus className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                  {/* Value column — full width */}
                  <div className="flex-1 min-w-0">
                    {renderFieldValue(field)}
                    {/* Duplicated field entries */}
                    {canDuplicateField(field) && duplicatedFields[field.key]?.map((dupVal, dupIdx) => (
                      <div key={dupIdx} className="flex items-center gap-1 mt-1.5">
                        <div className="relative flex-1">
                          <input
                            type="text"
                            value={dupVal}
                            onChange={e => updateDuplicate(field.key, dupIdx, e.target.value)}
                            className={`${inputClass} ${field.type === 'url' && dupVal ? 'pr-8' : ''}`}
                            placeholder={`${field.label} (${dupIdx + 2})`}
                          />
                          {field.type === 'url' && dupVal && (
                            <a
                              href={dupVal}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="absolute right-2 top-1/2 -translate-y-1/2 text-text-muted hover:text-accent-primary transition-colors"
                              title="Otwórz link"
                            >
                              <ExternalLink className="w-3.5 h-3.5" />
                            </a>
                          )}
                        </div>
                        <button
                          onClick={() => removeDuplicate(field.key, dupIdx)}
                          className="p-1 text-text-disabled hover:text-danger transition-colors shrink-0"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              );
              if (field.key !== 'createdAt') return [fieldRow];
              return [
                fieldRow,
                <div key="brandId" className="group/field">
                  <div className="flex items-start gap-3 py-2.5 border-b border-border-subtle hover:bg-surface-hover/20 transition-colors rounded-sm -mx-2 px-2">
                    <div className="flex items-center gap-2 w-40 shrink-0 pt-1.5">
                      <Tags className="w-3.5 h-3.5 text-text-muted" />
                      <span className="text-sm text-text-muted">Marka</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <Select
                        value={(form.brandId as string) || ''}
                        onChange={event => updateField('brandId', event.target.value || null)}
                        className={inputClass}
                      >
                        <option value="">Bez marki</option>
                        {brands.map(brand => <option key={brand.id} value={brand.id}>{brand.name}</option>)}
                      </Select>
                    </div>
                  </div>
                </div>,
              ];
            })}
          </div>

          {/* Add to Laboratorium */}
          <div className="px-6 py-4 border-t border-border">
            {hasLaboratory(product) ? (
              <Link
                to={`/marketing/laboratorium/produkt/${product.id}`}
                onClick={async event => {
                  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
                  event.preventDefault();
                  if (await saveNow()) {
                    onClose();
                    nav(`/marketing/laboratorium/produkt/${product.id}`);
                  }
                }}
                className={`${buttonVariants({ variant: 'secondary', size: 'md', fullWidth: true })} no-underline`}
              >
                <FlaskConical className="w-4 h-4" />
                Otwórz w Laboratorium
              </Link>
            ) : showLabSetup ? (
              <LabCreateWorkflowModal
                embedded
                showBrandSelect={false}
                product={{
                  id: product.id,
                  name: (form.name as string) || product.name,
                  brandId: (form.brandId as string) || null,
                }}
                onClose={() => setShowLabSetup(false)}
                onBusyChange={setLabWorkflowBusy}
                onStarted={(mode) => {
                  onClose();
                  nav(`/marketing/laboratorium/produkt/${product.id}${mode === 'scratch' ? '?autoGenerate=true' : ''}`);
                }}
              />
            ) : (
              <Button
                onClick={async () => { if (await saveNow()) setShowLabSetup(true); }}
                variant="primary"
                size="md"
                fullWidth
                leftIcon={<FlaskConical />}
              >
                Utwórz Laboratorium produktu
              </Button>
            )}
          </div>
        </div>

        {/* Footer — auto-save status left, delete right */}
        <div className="shrink-0 px-6 py-3 border-t border-border flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs">
            {saving && (
              <span className="flex items-center gap-1.5 text-text-secondary">
                <Loader2 className="w-3 h-3 animate-spin" /> Zapisywanie...
              </span>
            )}
            {saveStatus === 'success' && !saving && (
              <span className="flex items-center gap-1.5 text-emerald-400">
                <CheckCircle className="w-3.5 h-3.5" /> Zapisano
              </span>
            )}
            {saveStatus === 'error' && !saving && (
              <div className="flex items-center gap-2 text-danger" role="alert">
                <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                <span>{saveError}</span>
                <button type="button" onClick={() => { void saveNow(); }} className="shrink-0 underline">Ponów zapis</button>
              </div>
            )}
          </div>
          <button
            disabled={labWorkflowBusy}
            onClick={() => onDelete(product.id)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-danger hover:text-danger hover:bg-danger/10 rounded-lg transition-colors disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Trash2 className="w-3 h-3" /> Usuń produkt
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Editable Table Cell ────────────────────────────────────────────────────

function EditableCell({
  product,
  field,
  fieldOptions,
  onInlineSave,
  onRowClick,
}: {
  product: LabProduct;
  field: FieldConfig;
  fieldOptions: Record<string, FieldOption[]>;
  onInlineSave: (id: string, key: string, value: unknown) => void;
  onRowClick: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [selectOpen, setSelectOpen] = useState(false);
  const cellRef = useRef<HTMLTableCellElement>(null);

  const value = getProductValue(product, field.key);
  const isSelectField = field.key === 'status' || ['select', 'potential', 'testStatus'].includes(field.type);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isSelectField) {
      setSelectOpen(true);
    } else if (['text', 'url', 'textarea', 'number'].includes(field.type)) {
      setEditing(true);
    } else {
      onRowClick();
    }
  };

  const handleTextSave = (newVal: string) => {
    setEditing(false);
    if (newVal !== (value as string || '')) {
      onInlineSave(product.id, field.key, newVal);
    }
  };

  const handleSelectChange = (newVal: string) => {
    if (field.key === 'status') {
      onInlineSave(product.id, field.key, newVal || 'DRAFT');
    } else if (field.type === 'potential' && newVal === '') {
      onInlineSave(product.id, field.key, null);
    } else {
      onInlineSave(product.id, field.key, newVal);
    }
  };

  const getSelectOptions = (): { value: string; label: string; color: string }[] => {
    if (field.key === 'status') return LAB_STATUS_OPTIONS;
    if (field.type === 'potential') return POTENTIAL_OPTIONS;
    if (field.type === 'testStatus') return getTestStatusOptions(fieldOptions);
    const options = (fieldOptions[field.key] || []).map(o => ({ value: o.value, label: o.label, color: o.color }));
    return field.key === 'niche'
      ? [{ value: '', label: 'Brak kategorii', color: '#9ca3af' }, ...options]
      : options;
  };

  return (
    <td
      ref={cellRef}
      className="px-3 py-2 cursor-pointer"
      onClick={handleClick}
    >
      {editing ? (
        <InlineCellEditor
          value={(value as string) || ''}
          onSave={handleTextSave}
          onCancel={() => setEditing(false)}
        />
      ) : (
        field.key === 'name' ? (
          <div className="group/name flex min-w-[180px] items-center gap-2">
            <div className="min-w-0 flex-1">
              <CellValue product={product} field={field} fieldOptions={fieldOptions} />
            </div>
            <button
              type="button"
              onClick={event => { event.stopPropagation(); onRowClick(); }}
              className="inline-flex shrink-0 items-center gap-1 rounded-md border border-border bg-surface-hover px-1.5 py-1 text-[10px] font-medium text-text-secondary opacity-0 shadow-sm transition-all hover:border-border-strong hover:text-text-primary group-hover/name:opacity-100 focus-visible:opacity-100"
              aria-label={`Otwórz produkt ${product.name}`}
            >
              <ArrowUpRight className="h-3 w-3" />
              Otwórz
            </button>
          </div>
        ) : (
          <CellValue product={product} field={field} fieldOptions={fieldOptions} />
        )
      )}
      {selectOpen && cellRef.current && (
        <InlineCellSelect
          value={(value as string) || ''}
          options={getSelectOptions()}
          onChange={handleSelectChange}
          onClose={() => setSelectOpen(false)}
          anchorElement={cellRef.current}
        />
      )}
    </td>
  );
}

function LabStatusButton({
  product,
  onCreateLab,
  onOpenLab,
}: {
  product: LabProduct;
  onCreateLab: (product: LabProduct) => void;
  onOpenLab: (product: LabProduct) => void;
}) {
  const active = hasLaboratory(product);

  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        active ? onOpenLab(product) : onCreateLab(product);
      }}
      className={`inline-flex items-center justify-center w-7 h-7 rounded-lg border transition-all ${
        active
          ? 'bg-emerald-500/10 border-emerald-500/25 text-emerald-400 hover:bg-emerald-500/20'
          : 'bg-surface-hover/50 border-border-subtle text-text-disabled hover:text-accent-primary hover:border-accent-primary/25'
      }`}
      title={active ? 'Laboratorium utworzone' : 'Utwórz Laboratorium produktu'}
      aria-label={active ? 'Otwórz Laboratorium produktu' : 'Utwórz Laboratorium produktu'}
    >
      <FlaskConical className="w-3.5 h-3.5" />
    </button>
  );
}

function ProductGridCard({
  product,
  fieldOptions,
  onOpenProduct,
  onCreateLab,
  onOpenLab,
}: {
  product: LabProduct;
  fieldOptions: Record<string, FieldOption[]>;
  onOpenProduct: (product: LabProduct) => void;
  onCreateLab: (product: LabProduct) => void;
  onOpenLab: (product: LabProduct) => void;
}) {
  const activeLab = hasLaboratory(product);
  const hasKB = product.knowledgeBases?.some(kb => kb.isComplete);
  const cfg = getOptionDisplayConfig(getTestStatusOptions(fieldOptions), product.testStatus, 'PENDING');

  return (
    <article
      className="group bg-surface-raised rounded-lg border border-border hover:border-accent-primary/30 transition-all overflow-hidden cursor-pointer"
      onClick={() => onOpenProduct(product)}
    >
      <div className="aspect-[4/3] bg-surface-hover/40 border-b border-border-subtle flex items-center justify-center overflow-hidden">
        {product.imageUrl ? (
          <img src={product.imageUrl} alt={product.name} className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform" />
        ) : (
          <Package className="w-10 h-10 text-text-disabled" />
        )}
      </div>
      <div className="p-4 space-y-3">
        <div className="flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-text-primary truncate group-hover:text-accent-primary transition-colors">
              {product.name}
            </h3>
            <div className="flex items-center gap-2 mt-1">
              <span className="px-2 py-0.5 bg-surface-hover/70 rounded text-[10px] text-text-muted truncate max-w-[140px]">
                {product.niche || 'Brak kategorii'}
              </span>
              <span
                className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap"
                style={{ backgroundColor: cfg.bg, color: cfg.text }}
              >
                {cfg.label}
              </span>
            </div>
          </div>
          <LabStatusButton product={product} onCreateLab={onCreateLab} onOpenLab={onOpenLab} />
        </div>

        <div className="text-[10px] text-text-muted">
          <div className="rounded-lg bg-surface-hover/50 px-2 py-1.5">
            <span className="block text-text-disabled">Baza wiedzy</span>
            <span className={hasKB ? 'text-emerald-400' : 'text-text-secondary'}>
              {hasKB ? 'Gotowa' : 'Brak'}
            </span>
          </div>
        </div>

        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            activeLab ? onOpenLab(product) : onCreateLab(product);
          }}
          className={`w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium border transition-colors ${
            activeLab
              ? 'bg-emerald-500/10 border-emerald-500/25 text-emerald-400 hover:bg-emerald-500/20'
              : 'bg-accent-primary/10 border-accent-primary/25 text-accent-primary hover:bg-accent-primary/20'
          }`}
        >
          <FlaskConical className="w-3.5 h-3.5" />
          {activeLab ? 'Otwórz Laboratorium produktu' : 'Utwórz Laboratorium produktu'}
        </button>
      </div>
    </article>
  );
}

// ─── Main Page ──────────────────────────────────────────────────────────────

export function LabProductsPage({ sourceFilter, hunterContent }: { sourceFilter?: ReactNode; hunterContent?: ReactNode }) {
  const [brandRevision, setBrandRevision] = useState(0);
  const confirmDialog = useConfirmDialog();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const brandFilter = searchParams.get('brandId') || undefined;
  const unbrandedFilter = searchParams.get('unbranded') === 'true';
  const nicheFilter = searchParams.get('niche') || undefined;
  const uncategorizedFilter = searchParams.get('uncategorized') === 'true';
  const laboratoryView = searchParams.get('view') === 'laboratory' || searchParams.get('withLaboratory') === 'true';
  const withoutLaboratoryFilter = true;
  const databaseView = !laboratoryView && !hunterContent;
  const [products, setProducts] = useState<LabProduct[]>([]);
  const [fields, setFields] = useState<FieldConfig[]>([]);
  const [fieldOptions, setFieldOptions] = useState<Record<string, FieldOption[]>>({});
  const [brands, setBrands] = useState<LabBrandOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState<'list' | 'grid'>(() => {
    const saved = localStorage.getItem('labProductsViewMode');
    return saved === 'grid' ? 'grid' : 'list';
  });
  const [creating, setCreating] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<LabProduct | null>(null);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [deleting, setDeleting] = useState(false);
  const [createLabProduct, setCreateLabProduct] = useState<LabProduct | null>(null);
  const [draggedProductId, setDraggedProductId] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<{ id: string; placement: 'before' | 'after' } | null>(null);

  useEffect(() => {
    loadFieldConfig();
  }, []);

  useEffect(() => {
    loadProducts();
  }, [search, brandFilter, unbrandedFilter, nicheFilter, uncategorizedFilter, withoutLaboratoryFilter]);

  useEffect(() => {
    localStorage.setItem('labProductsViewMode', viewMode);
  }, [viewMode]);

  const loadFieldConfig = async () => {
    try {
      const [res, brandResponse] = await Promise.all([labApi.getFieldConfig(), labApi.getBrands()]);
      const data = res.data.data;
      setFields(normalizeLabProductFields(data.fields || []));
      setFieldOptions(data.fieldOptions || {});
      setBrands(brandResponse.data.data?.brands || []);
    } catch (err) {
      console.error('Error loading field config:', err);
    }
  };

  const loadProducts = useCallback(async () => {
    try {
      const res = await labApi.getProducts({
        search: search || undefined,
        limit: 200,
        creationSource: 'MANUAL',
        brandId: brandFilter,
        unbranded: unbrandedFilter || undefined,
        niche: nicheFilter,
        uncategorized: uncategorizedFilter || undefined,
        withoutLaboratory: withoutLaboratoryFilter || undefined,
      });
      setProducts(res.data.data);
    } catch (err) {
      console.error('Error loading products:', err);
    } finally {
      setLoading(false);
    }
  }, [search, brandFilter, unbrandedFilter, nicheFilter, uncategorizedFilter, withoutLaboratoryFilter]);

  const handleCreate = async () => {
    setCreating(true);
    try {
      await labApi.createProduct({
        name: 'Nowy produkt',
        brandId: brandFilter || null,
        niche: nicheFilter || undefined,
      });
      await loadProducts();
      updateFilters({ view: null, withLaboratory: null, source: 'manual' });
    } catch (err) {
      console.error('Error creating product:', err);
    } finally {
      setCreating(false);
    }
  };

  const handleCreateLab = async (product: LabProduct) => {
    setCreateLabProduct(product);
  };

  const handleOpenLab = (product: LabProduct) => {
    navigate(`/marketing/laboratorium/produkt/${product.id}`);
  };

  const handleDelete = async (id: string) => {
    if (!(await confirmDialog({
      title: 'Usuń produkt',
      message: 'Czy na pewno chcesz usunąć ten produkt?',
      confirmLabel: 'Usuń',
    }))) return;
    try {
      await labApi.deleteProduct(id);
      setSelectedProduct(null);
      loadProducts();
    } catch (err) {
      console.error('Error deleting:', err);
    }
  };

  const handleMassDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!(await confirmDialog({
      title: 'Usuń produkty',
      message: `Czy na pewno chcesz usunąć ${selectedIds.size} produktów?`,
      confirmLabel: 'Usuń',
    }))) return;
    setDeleting(true);
    try {
      await Promise.all(Array.from(selectedIds).map(id => labApi.deleteProduct(id)));
      setSelectedIds(new Set());
      setSelectionMode(false);
      await loadProducts();
    } catch (err) {
      console.error('Error mass deleting:', err);
    } finally {
      setDeleting(false);
    }
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === products.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(products.map(p => p.id)));
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Enum fields that must be null (not empty string) when cleared
  const ENUM_FIELDS = new Set(['potential', 'testStatus', 'status']);

  const handleSaveProduct = async (id: string, data: Record<string, unknown>) => {
    try {
      // Clean data: convert empty strings to null for enum fields
      const cleaned: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(data)) {
        if (ENUM_FIELDS.has(key) && value === '') {
          cleaned[key] = null;
        } else {
          cleaned[key] = value;
        }
      }
      // Filter empty transcripts and packages
      if (Array.isArray(cleaned.adTranscripts)) {
        cleaned.adTranscripts = (cleaned.adTranscripts as string[]).filter(t => t.trim());
      }
      if (Array.isArray(cleaned.pricePackages)) {
        cleaned.pricePackages = (cleaned.pricePackages as PricePackage[]).filter(p => p.name.trim() || p.oldPrice.trim() || p.newPrice.trim());
      }
      await labApi.updateProduct(id, cleaned);
      await loadProducts();
    } catch (err) {
      console.error('Błąd zapisu produktu:', err);
      throw err;
    }
  };

  const handleInlineSave = async (id: string, key: string, value: unknown) => {
    try {
      await labApi.updateProduct(id, { [key]: value });
      await loadProducts();
      if (selectedProduct?.id === id) {
        const freshRes = await labApi.getProduct(id);
        setSelectedProduct(freshRes.data.data);
      }
    } catch (err) {
      console.error('Error saving inline:', err);
    }
  };

  const handleRowClick = async (product: LabProduct) => {
    if (selectionMode) {
      toggleSelect(product.id);
      return;
    }
    try {
      const res = await labApi.getProduct(product.id);
      setSelectedProduct(res.data.data);
    } catch (err) {
      console.error('Error loading product:', err);
    }
  };

  const updateFilters = (changes: Record<string, string | null>) => {
    const next = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(changes)) {
      if (value) next.set(key, value);
      else next.delete(key);
    }
    setSearchParams(next, { replace: true });
  };

  const handleProductDrop = async (
    movedProductId: string,
    targetProductId: string,
    placement: 'before' | 'after',
  ) => {
    if (movedProductId === targetProductId) return;
    const previous = products;
    const next = products.filter(product => product.id !== movedProductId);
    const moved = products.find(product => product.id === movedProductId);
    const targetIndex = next.findIndex(product => product.id === targetProductId);
    if (!moved || targetIndex < 0) return;
    next.splice(targetIndex + (placement === 'after' ? 1 : 0), 0, moved);
    setProducts(next);
    try {
      await labApi.moveProduct(movedProductId, targetProductId, placement);
      await loadProducts();
    } catch (error) {
      console.error('Nie udało się zmienić kolejności produktów:', error);
      setProducts(previous);
    }
  };

  const tableFields = fields.filter(f => f.showInTable);
  const nicheOptions = fieldOptions.niche || [];
  const hasActiveFilters = Boolean(nicheFilter || uncategorizedFilter || brandFilter || unbrandedFilter || withoutLaboratoryFilter);
  const filterButtonClass = (active: boolean) => `shrink-0 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
    active
      ? 'border-accent-primary/40 bg-accent-primary/15 text-accent-primary'
      : 'border-border bg-surface-raised text-text-secondary hover:border-border-strong hover:text-text-primary'
  }`;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="animate-fade-in flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-text-primary">Baza produktów</h1>
          <p className="text-text-secondary mt-2">
            Poczekalnia produktów i przestrzeń do uruchamiania eksperymentów AI
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Link
            to="/marketing/laboratorium/avatary"
            className="flex items-center gap-2 px-4 py-2.5 bg-surface-raised border border-border rounded-lg text-text-secondary text-sm font-medium hover:text-text-primary hover:border-accent-primary/30 transition-colors"
          >
            <Users className="w-4 h-4" />
            Avatary UGC
          </Link>
          <Link
            to="/marketing/laboratorium/glosy"
            className="flex items-center gap-2 px-4 py-2.5 bg-surface-raised border border-border rounded-lg text-text-secondary text-sm font-medium hover:text-text-primary hover:border-accent-primary/30 transition-colors"
          >
            <Mic2 className="w-4 h-4" />
            Głosy
          </Link>
          {databaseView && <div className="flex items-center gap-1 rounded-lg border border-border bg-surface-raised p-1">
            <button
              type="button"
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-md transition-colors ${viewMode === 'list' ? 'bg-surface-hover text-text-primary' : 'text-text-muted hover:text-text-primary'}`}
              title="Widok listy"
              aria-label="Widok listy"
            >
              <List className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-md transition-colors ${viewMode === 'grid' ? 'bg-surface-hover text-text-primary' : 'text-text-muted hover:text-text-primary'}`}
              title="Widok kafli"
              aria-label="Widok kafli"
            >
              <LayoutGrid className="w-4 h-4" />
            </button>
          </div>}
          <LabBrandsPage mode="button" onCreated={() => { void loadFieldConfig(); setBrandRevision(value => value + 1); }} />
          <button
            onClick={handleCreate}
            disabled={creating}
            className="flex items-center gap-2 px-4 py-2.5 bg-accent-primary/15 border border-accent-primary/30 rounded-lg text-accent-primary text-sm font-medium hover:bg-accent-primary/25 transition-colors disabled:opacity-50"
          >
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Dodaj produkt
          </button>
        </div>
      </div>

      {/* Mass delete bar */}
      {databaseView && selectionMode && selectedIds.size > 0 && (
        <div className="flex items-center gap-3 p-3 bg-danger/10 border border-danger/20 rounded-lg animate-fade-in">
          <span className="text-sm text-danger">{selectedIds.size} zaznaczonych</span>
          <button
            onClick={handleMassDelete}
            disabled={deleting}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-danger/20 text-danger rounded-lg text-sm font-medium hover:bg-danger/30 transition-colors disabled:opacity-50"
          >
            {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
            Usuń zaznaczone
          </button>
        </div>
      )}

      {/* Search */}
      {databaseView && <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Szukaj produktów..."
          className="w-full pl-10 pr-10 py-2 bg-surface-raised border border-border rounded-lg text-text-primary text-sm focus:outline-none focus:border-accent-primary/50 hover:border-border-strong transition-colors"
        />
        {search && (
          <button
            onClick={() => setSearch('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-primary transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>}

      {/* Filters */}
      <div className="space-y-3 rounded-xl border border-border bg-surface-raised p-3">
        {sourceFilter}
        <div className={`flex min-w-0 items-center gap-3 ${sourceFilter ? "border-t border-border-subtle pt-3" : ""}`}>
          <span className="w-14 shrink-0 text-[11px] font-semibold uppercase tracking-wider text-text-muted">Widok</span>
          <button
            type="button"
            aria-pressed={!laboratoryView}
            onClick={() => updateFilters({ view: null, withLaboratory: null })}
            className={filterButtonClass(!laboratoryView)}
          >
            Baza produktów
          </button>
          <button
            type="button"
            aria-pressed={laboratoryView}
            onClick={() => updateFilters({ view: 'laboratory', withLaboratory: null })}
            className={filterButtonClass(laboratoryView)}
            title="Marki i produkty z utworzonym Laboratorium."
          >
            Laboratorium
          </button>
        </div>
        {databaseView && <><div className="flex min-w-0 items-center gap-3 border-t border-border-subtle pt-3">
          <span className="w-14 shrink-0 text-[11px] font-semibold uppercase tracking-wider text-text-muted">Nisza</span>
          <div className="flex min-w-0 flex-1 gap-2 overflow-x-auto pb-1">
            <button
              type="button"
              onClick={() => updateFilters({ niche: null, uncategorized: null })}
              className={filterButtonClass(!nicheFilter && !uncategorizedFilter)}
            >
              Wszystkie
            </button>
            {nicheOptions.map(option => (
              <button
                key={option.value}
                type="button"
                onClick={() => updateFilters({ niche: option.value, uncategorized: null })}
                className={filterButtonClass(nicheFilter === option.value)}
              >
                {option.label}
              </button>
            ))}
            <button
              type="button"
              onClick={() => updateFilters({ niche: null, uncategorized: 'true' })}
              className={filterButtonClass(uncategorizedFilter)}
            >
              Bez kategorii
            </button>
          </div>
        </div>
        <div className="flex min-w-0 items-center gap-3 border-t border-border-subtle pt-3">
          <span className="w-14 shrink-0 text-[11px] font-semibold uppercase tracking-wider text-text-muted">Marka</span>
          <div className="flex min-w-0 flex-1 gap-2 overflow-x-auto pb-1">
            <button
              type="button"
              onClick={() => updateFilters({ brandId: null, unbranded: null })}
              className={filterButtonClass(!brandFilter && !unbrandedFilter)}
            >
              Wszystkie
            </button>
            {brands.map(brand => (
              <button
                key={brand.id}
                type="button"
                onClick={() => updateFilters({ brandId: brand.id, unbranded: null })}
                className={filterButtonClass(brandFilter === brand.id)}
              >
                {brand.name}
              </button>
            ))}
            <button
              type="button"
              onClick={() => updateFilters({ brandId: null, unbranded: 'true' })}
              className={filterButtonClass(unbrandedFilter)}
            >
              Bez marki
            </button>
          </div>
        </div></>}
      </div>

      {laboratoryView ? <LabBrandsPage key={brandRevision} mode="grid" /> : hunterContent ? hunterContent : <>
      {/* Products */}
      {loading ? (
        <div className="text-center text-text-muted py-12">Ładowanie...</div>
      ) : products.length === 0 && (search || hasActiveFilters) ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <Search className="w-10 h-10 text-text-disabled mb-4" />
          <h3 className="text-lg font-semibold text-text-primary mb-1">Brak pasujących produktów</h3>
          <p className="text-sm text-text-secondary mb-4">
            {search ? <>Nie znaleziono produktów dla frazy <span className="text-accent-primary">„{search}”</span>.</> : 'W wybranych filtrach nie ma jeszcze produktów.'}
          </p>
          <button
            onClick={() => {
              setSearch('');
              updateFilters({ niche: null, uncategorized: null, brandId: null, unbranded: null });
            }}
            className="flex items-center gap-1.5 px-4 py-2 bg-surface-hover text-text-secondary rounded-lg text-sm hover:bg-surface-muted transition-colors border border-border"
          >
            <X className="w-3.5 h-3.5" />
            Wyczyść filtry
          </button>
        </div>
      ) : products.length === 0 ? (
        <EmptyState
          icon={Store}
          title="Brak produktów"
          description='Kliknij "Dodaj produkt", aby rozpocząć.'
          buttonLabel="Dodaj produkt"
          onButtonClick={handleCreate}
          buttonIcon={Plus}
        />
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
          {products.map(product => (
            <ProductGridCard
              key={product.id}
              product={product}
              fieldOptions={fieldOptions}
              onOpenProduct={handleRowClick}
              onCreateLab={handleCreateLab}
              onOpenLab={handleOpenLab}
            />
          ))}
        </div>
      ) : (
        <div className="bg-surface-raised rounded-lg border border-border">
          {/* Scrollable table */}
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-border-subtle">
                  <th className="w-10 sticky left-0 z-10 bg-surface-raised border-r border-border-subtle">
                    <button
                      onClick={() => {
                        if (selectionMode) {
                          // Toggle select all, or exit selection mode if already all selected
                          if (selectedIds.size === products.length) {
                            setSelectionMode(false);
                            setSelectedIds(new Set());
                          } else {
                            toggleSelectAll();
                          }
                        } else {
                          setSelectionMode(true);
                          setSelectedIds(new Set());
                        }
                      }}
                      className={`p-1.5 transition-colors ${selectionMode ? 'text-accent-primary' : 'text-text-disabled hover:text-text-secondary'}`}
                      title={selectionMode ? 'Zaznacz wszystkie / Wyłącz' : 'Zaznaczanie wielu'}
                    >
                      {selectionMode
                        ? (selectedIds.size === products.length
                          ? <CheckSquare className="w-3.5 h-3.5" />
                          : <Square className="w-3.5 h-3.5" />)
                        : <CheckSquare className="w-3.5 h-3.5" />}
                    </button>
                  </th>
                  <th className="px-2 py-2 text-left text-[11px] font-medium text-text-muted uppercase tracking-wider w-12">
                    Lab
                  </th>
                  {tableFields.map(field => (
                    <th
                      key={field.key}
                      className="px-3 py-2 text-left text-[11px] font-medium text-text-muted uppercase tracking-wider whitespace-nowrap"
                      style={field.width ? { width: field.width, minWidth: field.width } : undefined}
                    >
                      <div className="flex items-center gap-1.5">
                        <FieldIcon name={field.icon} className="w-3 h-3 text-text-disabled" />
                        {field.label}
                      </div>
                    </th>
                  ))}
                  <th className="w-8" />
                </tr>
              </thead>
              <tbody>
                {products.map((product) => (
                  <tr
                    key={product.id}
                    onDragOver={event => {
                      if (!draggedProductId || draggedProductId === product.id) return;
                      event.preventDefault();
                      const rect = event.currentTarget.getBoundingClientRect();
                      setDropTarget({
                        id: product.id,
                        placement: event.clientY < rect.top + rect.height / 2 ? 'before' : 'after',
                      });
                    }}
                    onDrop={event => {
                      event.preventDefault();
                      if (draggedProductId && dropTarget?.id === product.id) {
                        void handleProductDrop(draggedProductId, product.id, dropTarget.placement);
                      }
                      setDraggedProductId(null);
                      setDropTarget(null);
                    }}
                    className={`border-b border-border-subtle hover:bg-surface-hover/40 cursor-pointer transition-colors group ${
                      selectedIds.has(product.id) ? 'bg-accent-primary/[0.04]' : ''
                    } ${dropTarget?.id === product.id ? 'bg-accent-primary/[0.08]' : ''
                    }`}
                    onClick={() => handleRowClick(product)}
                  >
                    <td className="sticky left-0 z-10 bg-surface-raised border-r border-border-subtle px-1.5 text-center">
                      {selectionMode ? (
                        <button
                          onClick={(e) => { e.stopPropagation(); toggleSelect(product.id); }}
                          className="p-1.5 transition-all"
                        >
                          {selectedIds.has(product.id)
                            ? <CheckSquare className="w-3.5 h-3.5 text-accent-primary" />
                            : <Square className="w-3.5 h-3.5 text-text-disabled" />}
                        </button>
                      ) : (
                        <button
                          type="button"
                          draggable
                          onClick={event => event.stopPropagation()}
                          onDragStart={event => {
                            event.stopPropagation();
                            event.dataTransfer.effectAllowed = 'move';
                            event.dataTransfer.setData('text/plain', product.id);
                            setDraggedProductId(product.id);
                          }}
                          onDragEnd={() => { setDraggedProductId(null); setDropTarget(null); }}
                          className="cursor-grab p-1.5 text-text-disabled hover:text-text-secondary active:cursor-grabbing rounded transition-all"
                          title="Przeciągnij, aby zmienić kolejność"
                          aria-label={`Zmień kolejność produktu ${product.name}`}
                        >
                          <GripVertical className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </td>
                    <td className="px-2 py-2">
                      <LabStatusButton product={product} onCreateLab={handleCreateLab} onOpenLab={handleOpenLab} />
                    </td>
                    {tableFields.map(field => (
                      <EditableCell
                        key={field.key}
                        product={product}
                        field={field}
                        fieldOptions={fieldOptions}
                        onInlineSave={handleInlineSave}
                        onRowClick={() => handleRowClick(product)}
                      />
                    ))}
                    <td className="pr-2">
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDelete(product.id); }}
                        className="p-1 text-text-disabled hover:text-danger rounded opacity-0 group-hover:opacity-100 transition-all"
                        title="Usuń produkt"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Add new row button — outside overflow-x-auto so it stays centered during horizontal scroll */}
          <button
            onClick={handleCreate}
            disabled={creating}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm text-text-secondary hover:text-accent-primary hover:bg-accent-primary/5 border-t border-border-subtle transition-colors disabled:opacity-50"
          >
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Dodaj produkt
          </button>
        </div>
      )}

      </>}
      {/* Product popup */}
      {selectedProduct && (
        <ProductPopup
          key={`${selectedProduct.id}:${fields.map(field => field.key).join(',')}`}
          product={selectedProduct}
          fields={fields}
          fieldOptions={fieldOptions}
          brands={brands}
          onClose={() => setSelectedProduct(null)}
          onSave={handleSaveProduct}
          onDelete={handleDelete}
        />
      )}
      {createLabProduct && (
        <LabCreateWorkflowModal
          product={createLabProduct}
          onClose={() => setCreateLabProduct(null)}
          onStarted={(mode) => {
            const productId = createLabProduct.id;
            setCreateLabProduct(null);
            navigate(`/marketing/laboratorium/produkt/${productId}${mode === 'scratch' ? '?autoGenerate=true' : ''}`);
          }}
        />
      )}
    </div>
  );
}
